"use client";

import logger from "@/lib/logger";
import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	Clapperboard,
	Download,
	FileVideo,
	Loader,
	Pause,
	Play,
	Scissors,
	Upload,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type WaveSurfer from "wavesurfer.js";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { FileDropZone } from "@/components/FileDropZone";
import { useTranslations } from "next-intl";

interface MediaMetadata {
	duration: number;
	width?: number;
	height?: number;
	frameRate?: number;
	isAudio: boolean;
}

interface TrimSettings {
	startTime: number;
	endTime: number;
}

const VideoTrimmer = () => {
	const { ffmpegInstance, isLoaded, isLoading, hardwareInfo, error, loadFFmpeg } =
		useFFmpeg();
	const t = useTranslations("videoTrimmer");

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const [inputFile, setInputFile] = useState<File | null>(null);
	const [isClient, setIsClient] = useState(false);
	const [mediaURL, setMediaURL] = useState("");
	const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
	const waveformRef = useRef<HTMLDivElement>(null);
	const wavesurferRef = useRef<WaveSurfer | null>(null);
	const [mediaMetadata, setMediaMetadata] = useState<MediaMetadata | null>(
		null,
	);
	const [trimSettings, setTrimSettings] = useState<TrimSettings>({
		startTime: 0,
		endTime: 0,
	});
	const [outputURL, setOutputURL] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [statusLog, setStatusLog] = useState("");
	const [progress, setProgress] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [isDragging, setIsDragging] = useState<"start" | "end" | null>(null);

	const videoRef = useRef<HTMLVideoElement>(null);
	const timelineRef = useRef<HTMLDivElement>(null);


	// Track client-side mounting
	useEffect(() => {
		setIsClient(true);
	}, []);

	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (!isClient) return;
		
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			setStatusLog(t("status.ready"));
		} else {
			setStatusLog(t("status.initializing"));
		}
	}, [error, isLoading, isLoaded, isClient, t]);

	// Set up FFmpeg progress listener for trimming
	useEffect(() => {
		if (ffmpegInstance && isProcessing) {
			const handleProgress = ({ progress }: { progress: number }) => {
				setProgress(progress);
			};

			ffmpegInstance.on("progress", handleProgress);

			return () => {
				ffmpegInstance.off("progress", handleProgress);
			};
		}
	}, [ffmpegInstance, isProcessing]);

	const isAudioFile = (file: File): boolean => {
		return file.type.startsWith('audio/');
	};

	const initializeWaveSurfer = useCallback(async (audioUrl: string) => {
		if (!isClient || !waveformRef.current) return;

		try {
			// Clean up existing wavesurfer instance
			if (wavesurferRef.current) {
				wavesurferRef.current.destroy();
				wavesurferRef.current = null;
			}

			// Dynamically import WaveSurfer to reduce initial bundle size
			const WaveSurferModule = await import("wavesurfer.js");
			const WaveSurfer = WaveSurferModule.default;

			// Create new wavesurfer instance
			wavesurferRef.current = WaveSurfer.create({
				container: waveformRef.current,
				waveColor: '#3b82f6',
				progressColor: '#1d4ed8',
				cursorColor: '#ef4444',
				barWidth: 2,
				barRadius: 3,
				height: 120,
				normalize: true,
				fillParent: true,
				mediaControls: false,
				interact: true
			});

			// Set up event listeners first
			wavesurferRef.current.on('ready', () => {
				if (wavesurferRef.current) {
					const duration = wavesurferRef.current.getDuration();
					setMediaMetadata({
						duration,
						isAudio: true,
					});
					setTrimSettings({
						startTime: 0,
						endTime: duration,
					});
				}
			});

			wavesurferRef.current.on('error', (error) => {
				logger.error({ err: error }, "WaveSurfer error");
				setStatusLog(t("status.waveformError"));
			});

			// Load audio file after setting up listeners
			wavesurferRef.current.load(audioUrl);

			wavesurferRef.current.on('audioprocess', (time) => {
				setCurrentTime(time);
			});

			wavesurferRef.current.on('play', () => {
				setIsPlaying(true);
			});

			wavesurferRef.current.on('pause', () => {
				setIsPlaying(false);
			});

			wavesurferRef.current.on('seeking', (progress: number) => {
				if (wavesurferRef.current && progress !== undefined) {
					const time = progress * wavesurferRef.current.getDuration();
					setCurrentTime(time);
				}
			});
		} catch (error) {
			logger.error({ err: error }, "Failed to initialize WaveSurfer");
			setStatusLog(t("status.waveformFailed"));
		}
	}, [isClient, t]);

	// Initialize WaveSurfer only after client mounting and when audio file is loaded
	useEffect(() => {
		if (!isClient || !inputFile || !isAudioFile(inputFile) || !mediaURL) return;
		
		// Small delay to ensure DOM is ready
		const timer = setTimeout(() => {
			initializeWaveSurfer(mediaURL);
		}, 100);
		
		return () => clearTimeout(timer);
	}, [isClient, inputFile, mediaURL, initializeWaveSurfer]);

	// Cleanup wavesurfer on unmount and file changes
	useEffect(() => {
		return () => {
			if (wavesurferRef.current) {
				try {
					wavesurferRef.current.destroy();
				} catch (error) {
					logger.warn({ err: error }, "Error destroying WaveSurfer instance");
				} finally {
					wavesurferRef.current = null;
				}
			}
		};
	}, []);

	// Handle trim boundary checking for audio files
	useEffect(() => {
		if (isPlaying && mediaMetadata?.isAudio && wavesurferRef.current && trimSettings.endTime > 0) {
			if (currentTime >= trimSettings.endTime) {
				const duration = wavesurferRef.current.getDuration();
				if (duration > 0) {
					const startPosition = trimSettings.startTime / duration;
					wavesurferRef.current.seekTo(startPosition);
					setCurrentTime(trimSettings.startTime);
				}
			}
		}
	}, [isPlaying, currentTime, trimSettings.startTime, trimSettings.endTime, mediaMetadata?.isAudio]);

	// Cleanup audio element on file changes
	useEffect(() => {
		return () => {
			if (audioRef) {
				try {
					audioRef.pause();
					audioRef.src = '';
				} catch (error) {
					logger.warn({ err: error }, "Error cleaning up audio element");
				}
			}
		};
	}, [audioRef]);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setInputFile(file);
			setOutputURL("");
			const url = URL.createObjectURL(file);
			setMediaURL(url);
			setStatusLog(
				isAudioFile(file) ? t("status.audioLoaded", { name: file.name }) : t("status.videoLoaded", { name: file.name }),
			);

			// Reset trim settings when new file is loaded
			setTrimSettings({ startTime: 0, endTime: 0 });

			// If it's an audio file, set up audio element
			if (isAudioFile(file)) {
				const audio = new Audio(url);
				setAudioRef(audio);
				// WaveSurfer will be initialized by the useEffect above
			} else {
				setAudioRef(null);
				// Clean up wavesurfer for video files
				if (wavesurferRef.current) {
					wavesurferRef.current.destroy();
					wavesurferRef.current = null;
				}
			}
		}
	};

	const handleVideoLoadedMetadata = () => {
		if (videoRef.current) {
			const video = videoRef.current;
			setMediaMetadata({
				duration: video.duration,
				width: video.videoWidth,
				height: video.videoHeight,
				frameRate: 30, // Default framerate
				isAudio: false,
			});
			setTrimSettings({
				startTime: 0,
				endTime: video.duration,
			});
		}
	};


	const handleTimeUpdate = () => {
		if (videoRef.current) {
			const time = videoRef.current.currentTime;
			setCurrentTime(time);

			// If playing and reached end of trim, pause or loop back to start
			if (isPlaying && time >= trimSettings.endTime) {
				videoRef.current.currentTime = trimSettings.startTime;
				setCurrentTime(trimSettings.startTime);
			}
		}
		// For audio files, time updates are handled by wavesurfer events
	};

	const handlePlay = () => {
		setIsPlaying(true);
	};

	const handlePause = () => {
		setIsPlaying(false);
	};

	const togglePlayPause = useCallback(() => {
		if (videoRef.current) {
			if (isPlaying) {
				videoRef.current.pause();
			} else {
				// Start playback from trim start if current time is outside trim range
				if (
					currentTime < trimSettings.startTime ||
					currentTime >= trimSettings.endTime
				) {
					videoRef.current.currentTime = trimSettings.startTime;
				}
				videoRef.current.play();
			}
		} else if (wavesurferRef.current) {
			// Use WaveSurfer for audio playback control
			if (isPlaying) {
				wavesurferRef.current.pause();
			} else {
				// Start playback from trim start if current time is outside trim range
				if (
					currentTime < trimSettings.startTime ||
					currentTime >= trimSettings.endTime
				) {
					const duration = wavesurferRef.current.getDuration();
					if (duration > 0) {
						const startPosition = trimSettings.startTime / duration;
						wavesurferRef.current.seekTo(startPosition);
					}
				}
				wavesurferRef.current.play().catch((err) => logger.error({ err }, "WaveSurfer play error"));
			}
		} else if (audioRef) {
			// Fallback to direct audio element control
			if (isPlaying) {
				audioRef.pause();
			} else {
				if (
					currentTime < trimSettings.startTime ||
					currentTime >= trimSettings.endTime
				) {
					audioRef.currentTime = trimSettings.startTime;
				}
				audioRef.play().catch((err) => logger.error({ err }, "Audio play error"));
			}
		}
	}, [isPlaying, currentTime, trimSettings.startTime, trimSettings.endTime, audioRef]);

	const seekToTime = useCallback((time: number) => {
		if (videoRef.current) {
			videoRef.current.currentTime = time;
			setCurrentTime(time);
		} else if (wavesurferRef.current) {
			// Use WaveSurfer for audio seeking
			const duration = wavesurferRef.current.getDuration();
			if (duration > 0) {
				const position = time / duration;
				wavesurferRef.current.seekTo(position);
			}
		} else if (audioRef) {
			// Fallback to direct audio element control
			audioRef.currentTime = time;
			setCurrentTime(time);
		}
	}, [audioRef]);

	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		const ms = Math.floor((seconds % 1) * 100);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
	};

	const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!timelineRef.current || !mediaMetadata || isDragging) return;

		const rect = timelineRef.current.getBoundingClientRect();
		const clickX = event.clientX - rect.left;
		const percentage = clickX / rect.width;
		const time = percentage * mediaMetadata.duration;

		seekToTime(Math.max(0, Math.min(time, mediaMetadata.duration)));
	};

	const handleMarkerMouseDown = (
		markerType: "start" | "end",
		event: React.MouseEvent,
	) => {
		event.stopPropagation();
		setIsDragging(markerType);
	};

	const handleMarkerTouchStart = (
		markerType: "start" | "end",
		event: React.TouchEvent,
	) => {
		event.stopPropagation();
		setIsDragging(markerType);
	};

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			if (!isDragging || !timelineRef.current || !mediaMetadata) return;

			const rect = timelineRef.current.getBoundingClientRect();
			const clickX = event.clientX - rect.left;
			const percentage = Math.max(0, Math.min(clickX / rect.width, 1));
			const time = percentage * mediaMetadata.duration;

			if (isDragging === "start") {
				setTrimSettings((prev) => ({
					...prev,
					startTime: Math.max(0, Math.min(time, prev.endTime - 0.1)),
				}));
			} else if (isDragging === "end") {
				setTrimSettings((prev) => ({
					...prev,
					endTime: Math.max(
						prev.startTime + 0.1,
						Math.min(time, mediaMetadata.duration),
					),
				}));
			}
		},
		[isDragging, mediaMetadata],
	);

	const handleTouchMove = useCallback(
		(event: TouchEvent) => {
			if (!isDragging || !timelineRef.current || !mediaMetadata) return;

			// Prevent default to avoid scrolling
			event.preventDefault();

			const touch = event.touches[0];
			const rect = timelineRef.current.getBoundingClientRect();
			const clickX = touch.clientX - rect.left;
			const percentage = Math.max(0, Math.min(clickX / rect.width, 1));
			const time = percentage * mediaMetadata.duration;

			if (isDragging === "start") {
				setTrimSettings((prev) => ({
					...prev,
					startTime: Math.max(0, Math.min(time, prev.endTime - 0.1)),
				}));
			} else if (isDragging === "end") {
				setTrimSettings((prev) => ({
					...prev,
					endTime: Math.max(
						prev.startTime + 0.1,
						Math.min(time, mediaMetadata.duration),
					),
				}));
			}
		},
		[isDragging, mediaMetadata],
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(null);
	}, []);

	const handleTouchEnd = useCallback(() => {
		setIsDragging(null);
	}, []);

	useEffect(() => {
		if (isDragging) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			document.addEventListener("touchmove", handleTouchMove, { passive: false });
			document.addEventListener("touchend", handleTouchEnd);
			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
				document.removeEventListener("touchmove", handleTouchMove);
				document.removeEventListener("touchend", handleTouchEnd);
			};
		}
	}, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyPress = (event: KeyboardEvent) => {
			if ((!videoRef.current && !audioRef) || !mediaMetadata) return;

			switch (event.code) {
				case "Space":
					event.preventDefault();
					togglePlayPause();
					break;
				case "KeyI":
					if (event.ctrlKey || event.metaKey) {
						event.preventDefault();
						setTrimSettings((prev) => ({ ...prev, startTime: currentTime }));
					}
					break;
				case "KeyO":
					if (event.ctrlKey || event.metaKey) {
						event.preventDefault();
						setTrimSettings((prev) => ({ ...prev, endTime: currentTime }));
					}
					break;
				case "ArrowLeft":
					if (event.shiftKey) {
						event.preventDefault();
						seekToTime(Math.max(0, currentTime - 1));
					} else {
						event.preventDefault();
						seekToTime(Math.max(0, currentTime - 0.1));
					}
					break;
				case "ArrowRight":
					if (event.shiftKey) {
						event.preventDefault();
						seekToTime(Math.min(mediaMetadata.duration, currentTime + 1));
					} else {
						event.preventDefault();
						seekToTime(Math.min(mediaMetadata.duration, currentTime + 0.1));
					}
					break;
			}
		};

		document.addEventListener("keydown", handleKeyPress);
		return () => document.removeEventListener("keydown", handleKeyPress);
	}, [currentTime, mediaMetadata, togglePlayPause, audioRef, seekToTime]);

	// Helper function to get file extension safely
	const getFileExtension = (filename: string): string => {
		const parts = filename.toLowerCase().split(".");
		return parts.length > 1 ? parts[parts.length - 1] : "mp4";
	};

	// Helper function to determine optimal codec settings
	const getCodecSettings = (extension: string, isAudio: boolean): string[] => {
		if (isAudio) {
			switch (extension) {
				case "mp3":
					return ["-c:a", "libmp3lame"];
				case "wav":
					return ["-c:a", "pcm_s16le"];
				case "aac":
				case "m4a":
					return ["-c:a", "aac"];
				case "ogg":
					return ["-c:a", "libvorbis"];
				case "flac":
					return ["-c:a", "flac"];
				default:
					return ["-c:a", "aac"];
			}
		} else {
			switch (extension) {
				case "mp4":
				case "m4v":
					return ["-c:v", "libx264", "-c:a", "aac", "-preset", "fast"];
				case "webm":
					return ["-c:v", "libvpx-vp9", "-c:a", "libopus"];
				case "mov":
					return ["-c:v", "libx264", "-c:a", "aac", "-preset", "fast"];
				case "avi":
					return ["-c:v", "libx264", "-c:a", "aac"];
				case "mkv":
					return ["-c:v", "libx264", "-c:a", "aac"];
				default:
					return ["-c:v", "libx264", "-c:a", "aac", "-preset", "fast"];
			}
		}
	};

	const trimMedia = useCallback(async () => {
		if (!inputFile || !ffmpegInstance || !mediaMetadata) return;

		setIsProcessing(true);
		setOutputURL("");
		setProgress(0);
		setStatusLog(mediaMetadata.isAudio ? t("status.processingAudio") : t("status.processingVideo"));

		const inputExtension = getFileExtension(inputFile.name);
		const inputFileName = `input.${inputExtension}`;
		const outputFileName = `trimmed.${inputExtension}`;

		try {
			const fileSizeMB = inputFile.size / (1024 * 1024);
			const memoryLimitMB = hardwareInfo.memory * 1024;

			if (hardwareInfo.isLowEnd && fileSizeMB > memoryLimitMB * 0.3) {
				throw new Error(
					t("status.fileTooLarge", { size: fileSizeMB.toFixed(1) }),
				);
			}

			setStatusLog(t("status.loadingFile"));
			await ffmpegInstance.writeFile(inputFileName, await fetchFile(inputFile));

			const duration = trimSettings.endTime - trimSettings.startTime;
			const codecSettings = getCodecSettings(inputExtension, mediaMetadata.isAudio);

			// Try stream copy first for better performance, fall back to re-encoding if needed
			let command = [
				"-i",
				inputFileName,
				"-ss",
				trimSettings.startTime.toString(),
				"-t",
				duration.toString(),
				"-avoid_negative_ts",
				"make_zero",
				"-c",
				"copy",
				outputFileName,
			];

			setStatusLog(mediaMetadata.isAudio ? t("status.fastTrimAudio") : t("status.fastTrimVideo"));

			try {
				await ffmpegInstance.exec(command);

				// Check if output file was created successfully
				const testData = (await ffmpegInstance.readFile(
					outputFileName,
				)) as Uint8Array;
				if (testData.length < 1000) {
					// File is too small, likely corrupted - fall back to re-encoding
					throw new Error("Stream copy produced invalid output");
				}
			} catch {
				setStatusLog(mediaMetadata.isAudio ? t("status.reEncodingAudio") : t("status.reEncodingVideo"));

				// Clean up failed output file
				try {
					await ffmpegInstance.deleteFile(outputFileName);
				} catch {}

				// Use re-encoding with proper codecs
				command = [
					"-i",
					inputFileName,
					"-ss",
					trimSettings.startTime.toString(),
					"-t",
					duration.toString(),
					"-avoid_negative_ts",
					"make_zero",
					...codecSettings,
				];

				// Add web optimization for video files
				if (!mediaMetadata.isAudio) {
					command.push("-movflags", "+faststart");
				}

				command.push(outputFileName);

				await ffmpegInstance.exec(command);
			}

			setStatusLog(t("status.readingFile"));
			const data = (await ffmpegInstance.readFile(
				outputFileName,
			)) as Uint8Array;

			// Validate output file size
			if (data.length < 1000) {
				throw new Error(
					t("status.outputCorrupted"),
				);
			}

			const blob = new Blob([data as BlobPart], { type: inputFile.type });
			const url = URL.createObjectURL(blob);
			setOutputURL(url);
			setProgress(1);

			setStatusLog(
				mediaMetadata.isAudio
					? t("status.successAudio", { size: (data.length / 1024 / 1024).toFixed(2) })
					: t("status.successVideo", { size: (data.length / 1024 / 1024).toFixed(2) }),
			);
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : t("status.unknownError");
			setStatusLog(t("status.trimFailed", { error: errorMessage }));
			logger.error({ err: error }, "FFmpeg Trim Error");

			// Provide specific guidance based on error type
			if (
				errorMessage.includes("Stream copy") ||
				errorMessage.includes("corrupted")
			) {
				setStatusLog(
					t("status.tryDifferentPoints", { error: errorMessage }),
				);
			} else if (errorMessage.includes("codec")) {
				setStatusLog(
					t("status.formatNotSupported", { error: errorMessage }),
				);
			}

			setProgress(0);
		} finally {
			setIsProcessing(false);
			try {
				await ffmpegInstance.deleteFile(inputFileName);
				await ffmpegInstance.deleteFile(outputFileName);
			} catch {
				// Files may not exist, ignore cleanup errors
			}
		}
	}, [inputFile, ffmpegInstance, mediaMetadata, trimSettings, hardwareInfo, t]);


	return (
		<div className="max-w-7xl mx-auto min-h-screen p-2 sm:p-4">
			<div className="mx-auto p-2 sm:p-6">
				<header className="text-center mb-10">
					<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
						<Clapperboard
							className="h-8 w-8 me-4 text-destructive"
							aria-hidden="true"
						/>
						{t("title")}
					</h1>
					<p className="mt-4 text-muted-foreground text-lg font-bold">
						{t("subtitle")}
					</p>
				</header>

				{/* Status Bar */}
				<Alert
					className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
				>
					<AlertTriangle className="h-4 w-4" aria-hidden="true" />
					<AlertDescription className="font-bold">{statusLog}</AlertDescription>
				</Alert>

				<div className="flex flex-col gap-6">
					{/* File Upload */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Upload className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFile")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<FileDropZone accept="video/*,audio/*" disabled={!isClient || !isLoaded || isProcessing} onFiles={(files) => handleFileChange({ target: { files } } as any)} label={t("fileInput.dropLabel")} activeLabel={t("fileInput.dropActiveLabel")} />
							{inputFile && isClient && (
								<p className="mt-3 text-sm font-bold">
									<span className="text-primary">{inputFile.name}</span> (
									{(inputFile.size / 1024 / 1024).toFixed(2)} MB)
								</p>
							)}
						</CardContent>
					</Card>

					{/* Media Preview and Controls */}
					{mediaURL && isClient && (
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<FileVideo className="w-6 h-6" aria-hidden="true" />
									{t("steps.previewTrim")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<div className="space-y-4">
									{/* Media Player */}
									<div className="relative">
										{inputFile && !isAudioFile(inputFile) ? (
											<video
												ref={videoRef}
												src={mediaURL}
												onLoadedMetadata={handleVideoLoadedMetadata}
												onTimeUpdate={handleTimeUpdate}
												onPlay={handlePlay}
												onPause={handlePause}
												className="w-full max-h-96 bg-black rounded-lg"
												controls={false}
												suppressHydrationWarning
											/>
										) : (
											<div className="w-full h-48 bg-muted rounded-lg border-2 border-muted-foreground/20 overflow-hidden">
												{isClient ? (
													<div 
														ref={waveformRef}
														className="w-full h-full"
													/>
												) : (
													<div className="w-full h-full flex items-center justify-center text-muted-foreground">
														{t("preview.loadingWaveform")}
													</div>
												)}
											</div>
										)}
									</div>

									{/* Video Controls */}
									<div className="flex items-center justify-center gap-4">
										<Button
											onClick={togglePlayPause}
											variant="outline"
											size="lg"
											disabled={!mediaMetadata}
											aria-label={isPlaying ? t("preview.pause") : t("preview.play")}
										>
											{isPlaying ? (
												<Pause className="w-5 h-5" />
											) : (
												<Play className="w-5 h-5" />
											)}
										</Button>
										<span className="text-sm font-mono" suppressHydrationWarning>
											{formatTime(currentTime)} /{" "}
											{mediaMetadata
												? formatTime(mediaMetadata.duration)
												: "--:--"}
										</span>
									</div>

									{/* Timeline */}
									{mediaMetadata && (
										<div className="space-y-4">
											<div
												ref={timelineRef}
												className="relative h-12 bg-muted rounded-lg cursor-pointer border-2 border-muted-foreground/20 hover:border-muted-foreground/40 transition-colors"
												onClick={handleTimelineClick}
											>
												{/* Progress Background */}
												<div className="absolute inset-1 bg-primary/20 rounded-md"></div>

												{/* Trim Selection */}
												<div
													className="absolute top-1 bottom-1 bg-primary/40 rounded-md"
													style={{
														left: `${(trimSettings.startTime / mediaMetadata.duration) * 100}%`,
														width: `${((trimSettings.endTime - trimSettings.startTime) / mediaMetadata.duration) * 100}%`,
													}}
												></div>

												{/* Current Time Indicator */}
												<div
													className="absolute top-0 bottom-0 w-1 bg-red-500 rounded-full"
													style={{
														left: `${(currentTime / mediaMetadata.duration) * 100}%`,
													}}
													suppressHydrationWarning
												></div>

												{/* Start Marker */}
												<div
													className={`absolute top-0 bottom-0 w-3 bg-green-500 rounded-full ${isDragging === "start" ? "cursor-grabbing" : "cursor-grab"} hover:bg-green-400 transition-colors shadow-md touch-none`}
													style={{
														left: `calc(${(trimSettings.startTime / mediaMetadata.duration) * 100}% - 6px)`,
													}}
													onMouseDown={(e) => handleMarkerMouseDown("start", e)}
													onTouchStart={(e) => handleMarkerTouchStart("start", e)}
													title={t("preview.startMarkerTitle", { time: formatTime(trimSettings.startTime) })}
													suppressHydrationWarning
													role="slider"
													aria-label={t("preview.trimStartPoint")}
													aria-valuemin={0}
													aria-valuemax={mediaMetadata?.duration || 0}
													aria-valuenow={trimSettings.startTime}
													aria-valuetext={formatTime(trimSettings.startTime)}
												>
													<div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-4 border-transparent border-b-green-500"></div>
												</div>

												{/* End Marker */}
												<div
													className={`absolute top-0 bottom-0 w-3 bg-red-500 rounded-full ${isDragging === "end" ? "cursor-grabbing" : "cursor-grab"} hover:bg-red-400 transition-colors shadow-md touch-none`}
													style={{
														left: `calc(${(trimSettings.endTime / mediaMetadata.duration) * 100}% - 6px)`,
													}}
													onMouseDown={(e) => handleMarkerMouseDown("end", e)}
													onTouchStart={(e) => handleMarkerTouchStart("end", e)}
													title={t("preview.endMarkerTitle", { time: formatTime(trimSettings.endTime) })}
													suppressHydrationWarning
													role="slider"
													aria-label={t("preview.trimEndPoint")}
													aria-valuemin={0}
													aria-valuemax={mediaMetadata?.duration || 0}
													aria-valuenow={trimSettings.endTime}
													aria-valuetext={formatTime(trimSettings.endTime)}
												>
													<div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-4 border-transparent border-b-red-500"></div>
												</div>
											</div>

											{/* Trim Info */}
											<div className="flex items-center justify-center">
												<div className="text-sm font-mono font-bold" suppressHydrationWarning>
													{t("preview.trimDuration")}{" "}
													{formatTime(
														trimSettings.endTime - trimSettings.startTime,
													)}
												</div>
											</div>

											{/* Media Info */}
											<div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
												{!mediaMetadata.isAudio && (
													<div>
														<Label className="text-xs text-muted-foreground">
															{t("mediaInfo.resolution")}
														</Label>
														<div className="font-mono">
															{mediaMetadata.width}x{mediaMetadata.height}
														</div>
													</div>
												)}
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("mediaInfo.duration")}
													</Label>
													<div className="font-mono" suppressHydrationWarning>
														{formatTime(mediaMetadata.duration)}
													</div>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("mediaInfo.startTime")}
													</Label>
													<div className="font-mono" suppressHydrationWarning>
														{formatTime(trimSettings.startTime)}
													</div>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("mediaInfo.endTime")}
													</Label>
													<div className="font-mono" suppressHydrationWarning>
														{formatTime(trimSettings.endTime)}
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					)}

					{/* Trim Button */}
					{mediaMetadata && (
						<div className="flex justify-center">
							<Button
								onClick={trimMedia}
								size="lg"
								variant={isProcessing ? "outline" : "default"}
								disabled={
									!isLoaded ||
									!inputFile ||
									isProcessing ||
									trimSettings.startTime >= trimSettings.endTime
								}
								className="px-8 py-4 text-lg"
							>
								{isProcessing ? (
									<>
										<Loader
											className="w-5 h-5 me-3 animate-spin"
											aria-hidden="true"
										/>
										{mediaMetadata?.isAudio ? t("convert.trimmingAudio") : t("convert.trimmingVideo")}
									</>
								) : (
									<>
										<Scissors className="w-5 h-5 me-3" aria-hidden="true" />
										{mediaMetadata?.isAudio ? t("convert.trimAudio") : t("convert.trimVideo")}
									</>
								)}
							</Button>
						</div>
					)}

					{/* Progress Bar */}
					{isProcessing && (
						<Card className="mb-6 p-3 sm:p-6">
							<div className="flex justify-between items-center mb-4">
								<Badge variant="secondary" className="font-black text-lg">
									{mediaMetadata?.isAudio ? t("progress.trimmingAudio") : t("progress.trimmingVideo")}
								</Badge>
								<span className="text-lg font-black">
									{Math.round(progress * 100)}%
								</span>
							</div>
							<Progress
								value={progress * 100}
								className="mb-4"
								aria-label={t("progress.progressLabel", { percent: Math.round(progress * 100) })}
							/>
						</Card>
					)}

					{/* Download Section */}
					<Card className="p-3 sm:p-6 min-h-[12rem] flex flex-col justify-between">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Download className="w-6 h-6" aria-hidden="true" />
								{t("steps.downloadTrimmed", { mediaType: mediaMetadata?.isAudio ? t("mediaTypeAudio") : t("mediaTypeVideo") })}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0 flex-1 flex items-center justify-center">
							{outputURL ? (
								<div className="flex flex-col items-center justify-center text-center space-y-4">
									<Badge
										variant="secondary"
										className="text-lg font-black px-4 py-2"
									>
										{mediaMetadata?.isAudio ? t("download.trimmedAudioReady") : t("download.trimmedVideoReady")}
									</Badge>
									<Button asChild size="lg" className="px-6 py-3">
										<a
											href={outputURL}
											download={`trimmed_${inputFile?.name || (mediaMetadata?.isAudio ? "audio" : "video")}`}
										>
											<Download className="w-5 h-5 me-3" aria-hidden="true" />
											{mediaMetadata?.isAudio ? t("download.downloadTrimmedAudio") : t("download.downloadTrimmedVideo")}
										</a>
									</Button>
								</div>
							) : (
								<div className="text-center">
									{isProcessing ? (
										<p className="text-primary font-bold text-lg">
											{mediaMetadata?.isAudio ? t("download.processingAudio") : t("download.processingVideo")}
										</p>
									) : (
										<p className="text-muted-foreground font-bold">
											{mediaMetadata?.isAudio ? t("download.emptyAudio") : t("download.emptyVideo")}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Keyboard Shortcuts Help */}
				<Card className="mt-6 p-3 sm:p-6">
					<CardHeader className="p-0 mb-4">
						<CardTitle className="text-lg">{t("shortcuts.title")}</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
							<div className="space-y-2">
								<div className="flex justify-between">
									<span className="font-mono bg-muted px-2 py-1 rounded">
										{t("shortcuts.space")}
									</span>
									<span>{t("shortcuts.playPause")}</span>
								</div>
								<div className="flex justify-between">
									<span className="font-mono bg-muted px-2 py-1 rounded">
										{t("shortcuts.arrows")}
									</span>
									<span>{t("shortcuts.seekSmall")}</span>
								</div>
								<div className="flex justify-between">
									<span className="font-mono bg-muted px-2 py-1 rounded">
										{t("shortcuts.shiftArrows")}
									</span>
									<span>{t("shortcuts.seekLarge")}</span>
								</div>
							</div>
							<div className="space-y-2">
								<div className="flex justify-between">
									<span className="font-mono bg-muted px-2 py-1 rounded">
										{t("shortcuts.ctrlI")}
									</span>
									<span>{t("shortcuts.setStart")}</span>
								</div>
								<div className="flex justify-between">
									<span className="font-mono bg-muted px-2 py-1 rounded">
										{t("shortcuts.ctrlO")}
									</span>
									<span>{t("shortcuts.setEnd")}</span>
								</div>
								<div className="flex justify-between">
									<span className="font-mono bg-muted px-2 py-1 rounded">
										{t("shortcuts.dragMarkers")}
									</span>
									<span>{t("shortcuts.preciseTrimming")}</span>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Information */}
				<Alert className="mt-8">
					<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
					<AlertDescription className="font-bold">
						{t("privacy")}
					</AlertDescription>
				</Alert>
			</div>
		</div>
	);
};

export default VideoTrimmer;
